import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { BaseDialog } from "@/types/dialog";
import { Text } from "../ui/text";
import { Button } from "../ui/button";
import CircleCheckIcon from "../ui/icons/CircleCheckIcon";
import CircleUnCheckIcon from "../ui/icons/CircleUnCheckIcon";
import { useState } from "react";
import { useChatShareQuery } from "@/hooks/services/queries/ai-agent/use-chat-share-query";
import { Input } from "../ui/input";
import { toast } from "sonner";
import CloseIcon from "../ui/icons/CloseIcon";
import { AI_SHARE_PATHNAME } from "@/constants/pathname";

const ChatShareDialog = ({
  isOpen,
  onClose,
  chatId,
}: BaseDialog & { chatId: string }) => {
  const [isPrivate, setIsPrivate] = useState(true);
  const [isLinkGenerated, setIsLinkGenerated] = useState(false);

  const { data, isLoading } = useChatShareQuery({
    chatId,
    isPrivate,
    enabled: isLinkGenerated,
  });

  const handleSelectPrivate = () => {
    setIsPrivate(true);
    setIsLinkGenerated(false);
  };

  const handleSelectOpen = () => {
    setIsPrivate(false);
    setIsLinkGenerated(false);
  };

  const shareToken = data?.split("/")?.pop();
  const shareUrl = shareToken != null ? `${location.origin}${AI_SHARE_PATHNAME}/${shareToken}` : "";

  const handleCopy = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    toast.success("Copied to clipboard");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[408px] flex flex-col gap-[28px] rounded-lg p-[24px]">
        <DialogHeader className="text-start pl-4">
          <div className="flex justify-between items-center">
            <div className="flex flex-col ">
              <DialogTitle>
                <Text variant="title-xl" className="text-grey-20">
                  Share conversations
                </Text>
              </DialogTitle>
              <Text variant="title-sm" className="text-grey-40">
                Only messages so far are shared.
              </Text>
            </div>
            <DialogClose>
              <CloseIcon />
            </DialogClose>
          </div>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="border rounded-lg flex flex-col">
            <div className="flex justify-between items-center px-4 py-2">
              <div className="flex flex-col gap-2">
                <Text variant="body-md" className="text-grey-20">
                  Private
                </Text>
                <Text variant="body-md" className="text-grey-40">
                  Only you can access it.
                </Text>
              </div>
              <label className="cursor-pointer">
                <input
                  type="radio"
                  hidden
                  checked={isPrivate}
                  onChange={handleSelectPrivate}
                />
                <div className="w-[40px] h-[40px] flex justify-center items-center">
                  {isPrivate ? <CircleCheckIcon /> : <CircleUnCheckIcon />}
                </div>
              </label>
            </div>
            <div className="flex justify-between items-center px-4 py-2 border-t">
              <div className="flex flex-col gap-2">
                <Text variant="body-md" className="text-grey-20">
                  Open access
                </Text>
                <Text variant="body-md" className="text-grey-40">
                  Viewable to anyone with a link.
                </Text>
              </div>
              <label className="cursor-pointer">
                <input
                  type="radio"
                  hidden
                  checked={!isPrivate}
                  onChange={handleSelectOpen}
                />
                <div className="w-[40px] h-[40px] flex justify-center items-center">
                  {!isPrivate ? <CircleCheckIcon /> : <CircleUnCheckIcon />}
                </div>
              </label>
            </div>
          </div>
          <div className="px-4">
            <Text variant="label-md" className="text-grey-40">
              Please do not share personal information or third-party content
              without permission, and please check the{" "}
              <span className="underline">Usage Policy</span>.
            </Text>
          </div>
        </div>
        <div className="flex justify-end">
          {isLinkGenerated ? (
            <div className="flex items-center gap-2 pl-2 pr-2 py-2 bg-[#F8F8F8] w-full">
              <Input
                type="text"
                placeholder="Enter the shared link"
                value={shareUrl}
                className="text-[12px] text-grey-60 h-[16px] bg-transparent border-none truncate"
                readOnly
              />
              <Button
                variant={"primary"}
                className="px-4 py-3"
                onClick={handleCopy}
                disabled={isLoading || !shareUrl}
              >
                Copy the link
              </Button>
            </div>
          ) : (
            <Button
              variant={"primary"}
              className="px-4 py-3"
              onClick={() => setIsLinkGenerated(true)}
            >
              <Text variant="title-sm" className="text-white">
                Create a shared link
              </Text>
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChatShareDialog;
